import { DisplayUnitType, HorizontalAlignment, StyleUtil } from "@mcleod/core";
import { MouseEvent, Panel, Table, TableCell } from "@mcleod/components";
import { TimelineCalculator } from "./TimelineCalculator";
import { TimeBubble } from "./TimeBubble";

export class TimeMarker {
    private timelineHeaderCell: TableCell;
    private positionCalculator: TimelineCalculator;
    private precedingColumnWidth: number;
    private tableBodyMutationObserver: MutationObserver;
    private panelHeader: Panel;
    private panelHeaderTimeBubble: TimeBubble;
    private timeBubbleTooltip: TimeBubble;
    private panelLine: Panel;
    private showLineOnly = true;
    private timer: NodeJS.Timeout;

    constructor(timelineHeaderCell: TableCell, positionCalculator: TimelineCalculator) {
        this.timelineHeaderCell = timelineHeaderCell;
        this.positionCalculator = positionCalculator;
        this.build();
        this.setupListeners();
        this.place();
        this.table.addUnmountListener(() => {
            clearInterval(this.timer);
            this.startTimerOnMount();
        });
        this.startTimerOnMount();
    }

    private startTimerOnMount() {
        this.table.addMountListener(() => this.updateOnInterval());
    }

    private get table(): Table {
        return this.timelineHeaderCell.table;
    }

    private build() {
        if (this.showLineOnly !== true) {
            this.createHeader();
        }
        this.createLine();
        this.calculatePrecedingColumnWidth();
        this.setLineHeight();
    }

    private createHeader() {
        this.panelHeader = Panel.createNoMarginNoPaddingPanel({
            id: "panelTimeMarkerHeader",
            visible: false,
            align: HorizontalAlignment.CENTER,
            paddingLeft: 5,
            zIndex: 4
        });
        this.panelHeaderTimeBubble = new TimeBubble();
        this.panelHeader.add(this.panelHeaderTimeBubble, this.createTriangle());
        this.addHeaderToHeaderCell();
    }

    private createTriangle(): Panel {
        return Panel.createNoMarginNoPaddingPanel({
            id: "panelTimeMarkerTriangle",
            paddingLeft: 5,
            backgroundColor: "transparent",
            borderTopColor: "primary",
            borderTopWidth: 20,
            borderLeftColor: "transparent",
            borderLeftWidth: 10,
            borderRightColor: "transparent",
            borderRightWidth: 10,
            height: 0,
            width: 0
        });
    }

    private createLine() {
        this.panelLine = Panel.createNoMarginNoPaddingPanel({
            id: "panelTimeMarkerLine",
            backgroundColor: "primary",
            height: 0,
            width: this.showLineOnly ? 2 : 5,
            zIndex: 6,
            visible: false
        });
        if (this.showLineOnly === true) {
            this.panelLine.addMouseEnterListener((event: MouseEvent) => {
                this.timeBubbleTooltip = new TimeBubble({ id: "timeBubbleTooltip" });
                this.timeBubbleTooltip.style.position = "absolute";
                this.timeBubbleTooltip.top = 0;
                this.timeBubbleTooltip.left = -9999;
                document.body.appendChild(this.timeBubbleTooltip._element);
                this.timeBubbleTooltip.top = event.domEvent.y - (this.timeBubbleTooltip._element.clientHeight/2);
                this.timeBubbleTooltip.left = this.panelLine.bounds.right + 4;
            });
            this.panelLine.addMouseLeaveListener(() => {
                if (this.timeBubbleTooltip != null && document.body.contains(this.timeBubbleTooltip._element) === true)
                    document.body.removeChild(this.timeBubbleTooltip._element);
            });
        }
        this.addLineToTable();
    }

    private setupListeners() {
        this.listenForTableResize()
        this.listenForTableBodyMutations();
        this.listenForTableFilterChanges();
    }

    private listenForTableResize() {
        this.table.addResizeListener(() => {
            this.setLineHeight();
            this.calculatePrecedingColumnWidth();
            this.place();
        });
    }

    private listenForTableBodyMutations() {
        this.disconnectMutationObserver();
        this.tableBodyMutationObserver = new MutationObserver((mutations: MutationRecord[], observer: MutationObserver) => {
            this.setLineHeight();
        });
        this.tableBodyMutationObserver.observe(this.table["_tbody"], { childList: true, subtree: true });
        this.table.addUnmountListener(() => this.disconnectMutationObserver());
    }

    private listenForTableFilterChanges() {
        this.table.addFilterChangedListener(() => {
            if (document.getElementById("panelTimeMarkerLine") == null) {
                this.build();
                this.place();
            }
        });
    }

    private disconnectMutationObserver() {
        this.tableBodyMutationObserver?.disconnect();
    }

    private setLineHeight() {
        const height = this.table["_tbody"].scrollHeight;
        if (height > 0)
            this.panelLine.height = height;
    }

    private addHeaderToHeaderCell() {
        this.timelineHeaderCell.addWithAbsolutePosition(this.panelHeader, -9999, null, -2);
        this.panelHeader.visible = true;
    }

    private addLineToTable() {
        this.table.addToBodyWithAbsolutePosition(this.panelLine, -9999, 0);
        this.panelLine.visible = true;
    }

    private place() {
        const now = new Date();
        // leftRems indicates how far to the right the date is in the movement cell
        const leftRems = this.positionCalculator.getLeft(now);
        this.placeHeader(leftRems);
        this.placeLine(leftRems);
    }

    private placeHeader(leftRems: number) {
        if (this.panelHeader != null) {
            this.panelHeaderTimeBubble.setTime();
            // Need to center the header above the line
            const headerWidth = this.panelHeader._element.clientWidth;
            const remValue = StyleUtil.convertUnits(headerWidth, DisplayUnitType.PIXELS, DisplayUnitType.REMS);
            const headerWidthRems = Number(remValue);
            this.panelHeader.left = StyleUtil.numberAsRems(leftRems - (headerWidthRems/2));
        }
    }

    private placeLine(leftRems: number) {
        // Because the line is added to the table body and not the movement cell/column, we need to add the width of any
        // columns that are to the left of the movement column.
        leftRems += this.precedingColumnWidth;
        const remString = StyleUtil.numberAsRems(leftRems);
        this.panelLine.left = remString;
    }

    private calculatePrecedingColumnWidth() {
        let pixels = 0;
        for (const headingRowCell of this.table.tableHeadingRow.cells) {
            if (headingRowCell === this.timelineHeaderCell)
                break;
            pixels += headingRowCell._element.clientWidth;
        }
        const remValue = StyleUtil.convertUnits(pixels, DisplayUnitType.PIXELS, DisplayUnitType.REMS);
        this.precedingColumnWidth = Number(remValue);
    }

    private updateOnInterval() {
        this.timer = setInterval(() => this.place(), 30000);
    }
}
